import { useCallback, useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash-es';

import type { GenericDetails } from 'components/Input/Autocomplete';
import type { NormalizedOption } from 'components/Toolbar/utils';

import { Box, Button, Checkbox, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

interface MultiSelectProps {
    options: NormalizedOption[];
    selectedItem?: GenericDetails | null;
    setSelectedItem: (item: GenericDetails) => void;
    handleClose?: () => void;
    name?: string;
    label?: string;
    placeholder?: string;
}

const MultiSelect = ({ options, selectedItem, setSelectedItem, handleClose, name }: MultiSelectProps) => {
    const selectedIds = useMemo(() => {
        const id = selectedItem?.id;
        if (isEmpty(id)) return [];
        return String(id)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    }, [selectedItem?.id]);

    const [localSelected, setLocalSelected] = useState<Set<string>>(() => new Set(selectedIds));

    useEffect(() => {
        setLocalSelected(new Set(selectedIds));
    }, [selectedItem?.id]);

    const handleToggle = useCallback((optionId: string) => {
        setLocalSelected((prev) => {
            const next = new Set(prev);
            if (next.has(optionId)) {
                next.delete(optionId);
            } else {
                next.add(optionId);
            }
            return next;
        });
    }, []);

    const handleDone = useCallback(() => {
        const ids = Array.from(localSelected);
        const selectedOptions = options.filter((o) => ids.includes(String(o.id)));
        const id = ids.join(',');
        const nameStr = selectedOptions.map((o) => o.name).join(', ');
        setSelectedItem({ id, name: nameStr, value: id });
        handleClose?.();
    }, [localSelected, options, setSelectedItem, handleClose]);

    return (
        <Box sx={{ width: '100%', minWidth: 130, maxWidth: 320, p: 2 }}>
            <List dense disablePadding>
                {options.map((option) => {
                    const optionId = String(option.id);
                    const isChecked = localSelected.has(optionId);
                    return (
                        <ListItemButton
                            sx={{ p: 0 }}
                            key={optionId}
                            onClick={() => {
                                handleToggle(optionId);
                            }}
                            data-testid={`${name}_option_${optionId}`}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <Checkbox
                                    edge="start"
                                    checked={isChecked}
                                    tabIndex={-1}
                                    disableRipple
                                    slotProps={{ input: { 'aria-label': option.name } }}
                                />
                            </ListItemIcon>
                            <ListItemText primary={option.name} />
                        </ListItemButton>
                    );
                })}
            </List>
            <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleDone}
                data-locator={`${name}_multi_select_done`}
                sx={{ mt: 1 }}
            >
                DONE
            </Button>
        </Box>
    );
};

export { MultiSelect };
